<script setup lang="ts">
import { HomeFilled } from "@element-plus/icons-vue";
import { ElMessageBox } from "element-plus";
import { ref, toRef } from "vue";
import { useRouter } from "vue-router";
import {
  HousingApplication,
  HousingApplicationStatus,
  HousingSiteResult,
  HousingSiteResultType,
  Workflow,
} from "~/api";
import { applicationRouteMap } from "~/router/mainRouter";
import { useHousingAplicationWizard } from "~/YYC_Housing/composables/useHousingApplicationWizard";
import { ApplicationWizardStep } from "../types/plusTypes";
import ApplicationWorkflow from "./ApplicationWorkflow.vue";
import LocationPreferences from "./LocationPreferences.vue";

const props = defineProps<{
  application: HousingApplication;
  workflow?: Workflow;
  agent?: boolean;
}>();

const { stepStatusMap, handleSubmitApplication } = useHousingAplicationWizard(
  toRef(props, "application"),
  toRef(props, "workflow"),
);
const router = useRouter();

const preferences = ref<HousingSiteResult[]>(
  props.application.siteResults?.filter(
    (r) => r.resultType === HousingSiteResultType.PREFERRED,
  ) ?? [],
);

const handleSubmitApplicationWrapper = async () => {
  await handleSubmitApplication().then(() => {
    router.push({
      name: applicationRouteMap.Intake.children.Application.children
        .ApplicationHome.name,
    });
    ElMessageBox.alert(
      "Your application has been submitted. We will be in touch soon.",
      "Application Submitted",
      {
        confirmButtonText: "OK",
        type: "success",
      },
    );
  });
};
</script>

<template>
  <div class="ReviewAndSubmit">
    <div class="ApplicationHeading">
      <h2>
        <template v-if="agent">
          {{ application.applicantName }}
        </template>
        <template v-else> My Housing Application </template>
      </h2>

      <span>
        <ol>
          <li>Select the properties that you are interested in applying for</li>
          <li>Complete the required forms</li>
          <li>When you are done, come back to this page and click "Submit"</li>
        </ol>
      </span>
    </div>

    <LocationPreferences
      class="LocationPreferences ApplicationWizardStep"
      :preferences="preferences"
      :application="application"
      :class="{
        CompleteSection:
          stepStatusMap[ApplicationWizardStep.SelectLocations].completed,
      }"
    />

    <ApplicationWorkflow
      v-if="workflow"
      class="ApplicationWorkflow ApplicationWizardStep"
      :workflow="workflow"
      :application="application"
      :class="{
        CompleteSection:
          stepStatusMap[ApplicationWizardStep.ApplicationForm].completed,
      }"
    />
    <div class="buttonContainer">
      <el-button
        v-if="
          // User already submitted
          application.status === HousingApplicationStatus.DRAFT
        "
        :disabled="
          !stepStatusMap[ApplicationWizardStep.SelectLocations].completed ||
          !stepStatusMap[ApplicationWizardStep.ApplicationForm].completed
        "
        class="SubmitApplicationButton"
        type="primary"
        @click="handleSubmitApplicationWrapper"
      >
        Submit</el-button
      >

      <el-button
        v-else
        :icon="HomeFilled"
        class="GoHomeButton"
        type="primary"
        @click="$router.push('/')"
      >
        Go Home</el-button
      >
    </div>
  </div>
</template>

<style lang="less">
.el-message-box {
  .el-message-box__title {
    font-size: 1.5em;
  }
  .el-message-box__message p {
    font-size: 1.7em;
    line-height: normal;
  }
  .el-button {
    font-size: 1em;
  }
}

.ReviewAndSubmit {
  display: grid;
  grid-template-areas:
    "heading heading heading"
    "location-preferences location-preferences location-preferences"
    "application-form application-form application-form"
    "submit-button submit-button submit-button";
  row-gap: 16px;

  grid-template-columns: 1fr 1fr 1fr;
  height: fit-content;
  width: 100%;
  border-radius: 5px;

  // Uncomment for border styled completion marking
  // border-width: 2px;
  // border-color: var(--el-color-warning);
  // background-color: white;
  // &.CompleteSection {
  //   background-color: white;
  //   border-width: 2px;
  //   border-color: var(--el-color-success);
  // }

  .ApplicationHeading {
    grid-area: heading;
    @media (max-width: 500px) {
      font-size: 0.8em;
    }
  }

  .LocationPreferences {
    grid-area: location-preferences;
  }

  .ApplicationWorkflow {
    grid-area: application-form;
  }

  .ApplicationWizardStep {
    background-color: #f7f2b9;
    padding: 1rem 1.5rem;

    padding: 1rem 1.5rem;
    border-radius: 5px;
    border: 1px solid var(--el-color-primary-light-5);
    font-size: var(--el-font-size-basic);
    color: black;

    &.CompleteSection {
      background-color: #bbe8b9;
    }
  }

  .buttonContainer {
    display: flex;
    justify-content: center;
    gap: 20px;
    grid-area: submit-button;

    .SubmitApplicationButton,
    .GoHomeButton {
      width: min-content;
    }
  }
}
</style>
